import { ChatUser, UserAuthToken, UserGroup, UsersListFilters } from 'services/users/types'
import actions from './actions'

/** Loose pagination shape — mirrors the API's `meta.pagination` (see
 * services/users/types.ts `PaginationMeta`), but several sagas reset it to `[]`
 * or `{}` on empty/error, so callers must not assume every field is present. */
export interface LoosePagination {
  current_page?: number
  total?: number
  per_page?: number
  count?: number
  total_pages?: number
}

export interface UsersState {
  usersList: ChatUser[]
  usersActive: ChatUser | unknown[]
  usersInactiveList: ChatUser[]
  userSearchText: string
  userInactiveSearchText: string
  usersListTotal: number
  usersListInactiveTotal: number
  usersListPagination: LoosePagination
  usersListInactivePagination: LoosePagination
  /** Last filters applied via `USERS_LIST` — lets the filter UI rehydrate on remount (ENG-34725). */
  usersFilters: Record<string, unknown>
  // App the saved filters belong to, so they never leak into another app.
  usersFiltersAppId?: string | number
  inactiveUsersFilters: UsersListFilters
  areUsersLoading: boolean

  // Active/inactive tab badge counts (from the `count=1` users endpoint)
  usersActiveCount: number
  usersInactiveCount: number
  usersTotalCount: number
  areUserCountsLoading: boolean

  // Import-users request modal
  currentIntentRequest: string

  // AI agent users listing
  aiAgentUserList: ChatUser[]
  aiAgentUserListTotal: number
  aiAgentUserListText: string
  aiAgentUserSearchText: string
  areAIAgentUsersLoading: boolean
  aiAgentUserListPagination: LoosePagination

  // User-detail page: auth tokens
  uAuthTokensList: UserAuthToken[]
  uAuthTokensListTotal: number
  uAuthTokensListPagination: LoosePagination
  uAuthTokenSearchText: string

  // User-detail page: this user's friends + add-friend modal
  uFriendsList: ChatUser[]
  uFriendsListTotal: number
  uFriendsListPagination: LoosePagination
  userFriendSearchText: string
  friendUserSearchText: string
  userNonFriendsList: ChatUser[]
  nonFriendList: ChatUser[]
  nonFriendListTotal: number
  nonFriendListPagination: LoosePagination
  nonFriendSearchText: string

  // User-detail page: this user's groups + add-to-group modal
  uGroupsList: UserGroup[]
  uGroupsListTotal: number
  uGroupsListPagination: LoosePagination
  userGroupSearchText: string
  groupSearchText: string
  groupUserSearchText: string
  userNonGroupsList: unknown[]
  nonGroupList: unknown[]
  nonGroupListTotal: number
  nonGroupListPagination: LoosePagination
  nonGroupSearchText: string
}

const initialState: UsersState = {
  usersList: [],
  usersActive: [],
  usersInactiveList: [],
  userSearchText: '',
  userInactiveSearchText: '',
  usersListTotal: 0,
  usersListInactiveTotal: 0,
  usersListPagination: {},
  usersListInactivePagination: {},
  usersFilters: {},
  inactiveUsersFilters: {},
  areUsersLoading: false,

  usersActiveCount: 0,
  usersInactiveCount: 0,
  usersTotalCount: 0,
  areUserCountsLoading: false,

  currentIntentRequest: '',

  aiAgentUserList: [],
  aiAgentUserListTotal: 0,
  aiAgentUserListText: '',
  aiAgentUserSearchText: '',
  areAIAgentUsersLoading: false,
  aiAgentUserListPagination: {},

  uAuthTokensList: [],
  uAuthTokensListTotal: 0,
  uAuthTokensListPagination: {},
  uAuthTokenSearchText: '',

  uFriendsList: [],
  uFriendsListTotal: 0,
  uFriendsListPagination: {},
  userFriendSearchText: '',
  friendUserSearchText: '',
  userNonFriendsList: [],
  nonFriendList: [],
  nonFriendListTotal: 0,
  nonFriendListPagination: {},
  nonFriendSearchText: '',

  uGroupsList: [],
  uGroupsListTotal: 0,
  uGroupsListPagination: {},
  userGroupSearchText: '',
  groupSearchText: '',
  groupUserSearchText: '',
  userNonGroupsList: [],
  nonGroupList: [],
  nonGroupListTotal: 0,
  nonGroupListPagination: {},
  nonGroupSearchText: '',
}

interface UsersAction {
  type: string
  payload?: Partial<UsersState>
}

export default function usersReducer(
  state: UsersState = initialState,
  action: UsersAction = { type: '' },
): UsersState {
  switch (action.type) {
    case actions.SET_STATE:
      return { ...state, ...action.payload }
    default:
      return state
  }
}
